import { RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';

import { Button } from './Button';

/**
 * Registra el service worker y avisa cuando hay una versión nueva esperando.
 *
 * Con `registerType: 'prompt'` el SW nuevo no toma el control por su cuenta:
 * espera a que el usuario acepte, para no recargar la página en mitad de un
 * entrenamiento.
 */
export function UpdatePrompt() {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [updating, setUpdating] = useState(false);
  const updateRef = useRef<((reload?: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    // En StrictMode el efecto corre dos veces en desarrollo; registrar una sola.
    if (updateRef.current !== null) return;
    updateRef.current = registerSW({
      immediate: true,
      onNeedRefresh() {
        setNeedRefresh(true);
      },
    });
  }, []);

  if (!needRefresh) return null;

  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-0 z-40 mx-auto mb-[calc(4.5rem+env(safe-area-inset-bottom,0px))] w-[calc(100%-2rem)] max-w-app rounded-2xl border border-line bg-surface-overlay p-3 shadow-lg md:mr-6 md:mb-6 md:w-96"
    >
      <div className="flex items-center gap-3">
        <RefreshCw className="size-5 shrink-0 text-brand" aria-hidden="true" />
        <p className="min-w-0 flex-1 text-sm">Hay una versión nueva de la app.</p>
        <Button
          loading={updating}
          onClick={() => {
            setUpdating(true);
            void updateRef.current?.(true);
          }}
        >
          Actualizar
        </Button>
      </div>
    </div>
  );
}
